import React from "react";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import CardBase from "connectui/component/display/card/CardBase";
import SvgIcon from "connectui/component/display/SvgIcon";
import Text from "connectui/component/display/Text";
import ButtonText from "connectui/component/input/button/ButtonText";
import LightButtonBase from "connectui/component/input/button/base/LightButtonBase";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import EmptyButtonBase from "connectui/component/input/button/base/EmptyButtonBase";
import {ReactComponent as EmailIcon} from "connectui/icons/email.svg";
import {ReactComponent as EnsoIcon} from "connectui/icons/enso-logo.svg";
import {ReactComponent as UserIcon} from "connectui/icons/user2.svg";
import {ReactComponent as EditIcon} from "connectui/icons/edit.svg";
import {useTheme} from "connectui/theme/ThemeProvider";

import {messagingSettingsStageEnum} from "../../BaseMessagingSettingsPanel";
import StatusChip from "../chips/StatusChip";
import Skeleton from "../../../../../../connectui/component/display/Skeleton";
import FlexContainer from "../../../../../../connectui/component/layout/FlexContainer";
import Container from "../../../../../../connectui/component/layout/Container";

const ManagedEmailItem = ({email, theme, setStage}) => {
  return (
    <Container
      style={{
        width: "100%",
        height: "100%",
        justifyContent: "flex-start",
        gap: 2,
        display: "inline-flex",
      }}
    >
      <Container
        style={{
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          display: "flex",
        }}
      >
        <Container
          style={{
            width: 16,
            height: 16,
            position: "relative",
            alignContent: "center",
            verticalAlign: "middle",
          }}
        >
          <SvgIcon
            component={UserIcon}
            sx={{
              alignItems: "center",
              width: "16px",
              height: "16px",
            }}
          />
        </Container>
        <Text
          variant={"textMD"}
          sx={{
            paddingRight: theme.spacing(1),
          }}
        >
          {email.managed_email}
        </Text>
      </Container>
      {!email.capabilities?.includes("send") ||
      !email.capabilities?.includes("receive") ? (
        <EmptyButtonBase
          onClick={() => window.Intercom("showArticle", 9893967)}
        >
          <StatusChip
            text={"Setup Required"}
            type={"warning"}
            clickable={true}
          />
        </EmptyButtonBase>
      ) : (
        <StatusChip text={"Active"} type={"warning"} />
      )}
      <ButtonIcon
        buttonBase={EmptyButtonBase}
        icon={
          <SvgIcon
            component={EditIcon}
            sx={{
              color: theme.colors.primary,
              width: "16px",
              height: "16px",
            }}
          />
        }
        sx={{
          padding: theme.spacing(0.5),
          marginLeft: theme.spacing(1),
        }}
        onClick={() =>
          setStage({
            id: messagingSettingsStageEnum.email,
            data: {
              email: email,
            },
          })
        }
      />
    </Container>
  );
};

const EmailCard = ({setStage, data, ...props}) => {
  const {theme} = useTheme();
  const {t} = useTranslation();

  const userProfileLoading = useSelector(
    (state) => state.defaultReducer.loading.profile,
  );

  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const replyEmail = user_profile?.reply_email;
  const receiveEmails =
    user_profile?.managed_emails?.filter((e) =>
      e.capabilities?.includes("receive"),
    ) || [];
  const managedEmails = user_profile?.managed_emails;

  const getCardDescription = () => {
    return (
      <Text variant={"textSM"} sx={{color: theme.colors.textSecondary}}>
        Set up your email to send personalized booking confirmations, updates,
        and other guest communications
      </Text>
    );
  };

  const getCardContent = () => {
    if (userProfileLoading) {
      return (
        <FlexContainer
          flexDirection={"column"}
          gap={theme.spacing(3)}
          alignItems="flex-start"
        >
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} width="100%" height={"24px"} />
          ))}
        </FlexContainer>
      );
    }

    return (
      <FlexContainer
        flexDirection={"column"}
        alignItems={"flex-start"}
        gap={theme.spacing(3)}
      >
        {replyEmail && (
          <Container>
            <FlexContainer
              justifyContent="flex-start"
              gap={theme.spacing(1)}
              style={{
                width: "100%",
                height: "100%",
                display: "inline-flex",
              }}
            >
              <FlexContainer
                justifyContent={"center"}
                alignItems={"center"}
                gap={theme.spacing(2)}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    position: "relative",
                    alignContent: "center",
                  }}
                >
                  <SvgIcon
                    component={EnsoIcon}
                    sx={{
                      width: "12px",
                      height: "12px",
                      color: theme.colors.textTertiary,
                    }}
                  />
                </div>
                <Text
                  variant={"textMD"}
                  sx={{
                    color: receiveEmails.length
                      ? "rgba(0, 0, 0, 0.38)"
                      : "inherit",
                    paddingRight: theme.spacing(1),
                  }}
                >
                  {replyEmail}
                </Text>
              </FlexContainer>
              {receiveEmails.length ? (
                <StatusChip text={"Disabled"} type={"warning"} />
              ) : (
                <StatusChip text={"Connected"} type={"success"} />
              )}
            </FlexContainer>
            <Text variant={"textXS"} sx={{color: theme.colors.textSecondary}}>
              {receiveEmails.length
                ? t("managed-email-default-email-disabled")
                : t("managed-email-default-email-enabled")}
            </Text>
          </Container>
        )}
        {managedEmails?.map((e) => (
          <ManagedEmailItem
            key={e.managed_email}
            email={e}
            theme={theme}
            setStage={setStage}
          />
        ))}
      </FlexContainer>
    );
  };

  const getActionRow = () => {
    return (
      <ButtonText
        skeleton={{
          enabled: userProfileLoading,
          width: "150px",
          height: "36px",
        }}
        typographyVariant="textSM"
        weight="semibold"
        color="primary"
        onClick={() =>
          setStage({
            id: messagingSettingsStageEnum.email,
            data: {
              email: null,
            },
          })
        }
        buttonBase={LightButtonBase}
        label="Add Email"
      />
    );
  };

  return (
    <CardBase
      icon={
        <SvgIcon component={EmailIcon} sx={{width: "32px", height: "32px"}} />
      }
      title={<Text variant={"textLG"}>Emails</Text>}
      description={getCardDescription()}
      inlineContent={getCardContent()}
      actionRow={getActionRow()}
      {...props}
    />
  );
};

export default EmailCard;
